import CollectionSidebar from '@archetype-themes/modules/collection-sidebar'
import AjaxRenderer from '@archetype-themes/utils/ajax-renderer'
import { debounce } from '@archetype-themes/utils/utils'
import { EVENTS } from '@archetype-themes/utils/events'

class ItemGrid extends HTMLElement {
  constructor() {
    super()
    this.abortController = new AbortController()

    // Track "Show More" state
    this.isLoading = false
    this.noMoreProducts = false
    this.currentPage = 1
    this.totalPages = 1

    // For filters
    this.isAnimating = false
    this.isStickyHeader = false

    this.selectors = {
      // FILTER / SORT
      sortSelect: '#SortBy',
      viewChange: '.grid-view-btn',
      productGrid: '.product-grid',
      collectionGrid: '.collection-grid__wrapper',
      sidebar: '#CollectionSidebar',
      activeTagList: '.tag-list--active-tags',
      tags: '.tag-list input',
      activeTags: '.tag-list a',
      tagsForm: '.filter-form',
      filterBar: '.collection-filter',
      priceRange: '.price-range',
      trigger: '.collapsible-trigger',

      // LOAD MORE
      showMoreButton: '.load-more-btn',        // The button for "Load More"
      products: '.product-grid [id^="ProductCard-"]' 
        // CSS selector for each product card, adapt if needed
    }

    this.classes = {
      activeTag: 'tag--active',
      removeTagParent: 'tag--remove',
      collapsibleContent: 'collapsible-content',
      isOpen: 'is-open'
    }

    // For existing AjaxRenderer-based filter code
    this.sectionId = this.getAttribute('data-section-id')
    this.ajaxRenderer = new AjaxRenderer({
      sections: [{ sectionId: this.sectionId, nodeId: 'AjaxContent' }],
      onReplace: this.onReplaceAjaxContent.bind(this)
    })
  }

  connectedCallback() {
    console.log('[ItemGrid] connectedCallback fired')

    this.abortController = new AbortController()
    this.init()

    if (document.querySelector('header[data-sticky="true"]')) {
      this.setFilterStickyPosition()
    }

    document.addEventListener(EVENTS.stickyHeaderChange, this.handleStickyHeaderChange.bind(this), {
      signal: this.abortController.signal
    })

    document.addEventListener(EVENTS.toggleMobileFilters, this.handleToggleMobileFilters.bind(this), {
      signal: this.abortController.signal
    })
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  init() {
    console.log('[ItemGrid] init()')

    this.initSort()
    this.initFilters()
    this.initPriceRange()
    this.initGridOptions()

    this.initShowMore()

    this.sidebar = new CollectionSidebar()
  }

  // --------------------------------------------------------------------------
  // "SHOW MORE" (PLAIN FETCH)
  // --------------------------------------------------------------------------
  initShowMore() {
    console.log('[initShowMore] called.')
    const button = this.querySelector(this.selectors.showMoreButton)
    console.log('[initShowMore] button found:', button)

    if (!button) {
      console.log('[initShowMore] No "Show More" button found, skipping.')
      return
    }

    // Grab data attributes for current/total pages
    const cp = parseInt(button.dataset.currentPage || '1', 10)
    const tp = parseInt(button.dataset.totalPages || '1', 10)
    console.log('[initShowMore] currentPage from data:', cp, 'totalPages from data:', tp)

    this.currentPage = cp
    this.totalPages = tp

    // If we are already at or beyond the total pages
    if (this.currentPage >= this.totalPages) {
      console.log('[initShowMore] currentPage >= totalPages, hiding button.')
      button.style.display = 'none'
      return
    }

    console.log('[initShowMore] Adding click handler for "Show More".')
    button.addEventListener('click', this.handleShowMore.bind(this), {
      signal: this.abortController.signal
    })
  }

handleShowMore(evt) {
  evt.preventDefault()

  if (this.isLoading || this.noMoreProducts) return

  const button = this.querySelector('.load-more-btn')
  const productGrid = this.querySelector('.product-grid')
  if (!button || !productGrid) return

  // Pull localized strings
  const loadText = button.dataset.loadText || 'Show More'
  const loadingText = button.dataset.loadingText || 'Loading...'

  // Read data-current-page, data-total-pages
  let currentPage = parseInt(button.dataset.currentPage, 10) || 1
  const totalPages = parseInt(button.dataset.totalPages, 10) || 1

  // If we’re already at the last page or beyond
  if (currentPage >= totalPages) {
    button.style.display = 'none'
    this.noMoreProducts = true
    return
  }

  // Show "Loading..." in the user’s language
  this.isLoading = true
  button.textContent = loadingText

  // Build next-page URL from existing location
  const url = new URL(window.location.href)
  const nextPage = currentPage + 1
  url.searchParams.set('page', nextPage)    // Overwrite or add ?page=...
  const nextPageUrl = url.toString()

  fetch(nextPageUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error, status ${response.status}`)
      }
      return response.text()
    })
    .then((htmlString) => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlString, 'text/html')

      // Grab new product elements
      let grid = doc.getElementById('AjaxContent');
      const newProductEls = [...grid.querySelectorAll('[data-product-grid-item]')]
      if (!newProductEls.length) {
        // No new products => hide button
        this.noMoreProducts = true
        button.style.display = 'none'
        return
      }

      // Append them to .product-grid
      newProductEls.forEach((tile) => {
        const parentGridItem = tile.closest('.grid-item.grid-product') || tile
        productGrid.appendChild(parentGridItem)
      })

      // Bump current page
      currentPage++
      button.dataset.currentPage = currentPage   // So next time, it increments from there

      // If we’re now at or beyond total pages, hide button
      if (currentPage >= totalPages) {
        this.noMoreProducts = true
        button.style.display = 'none'
      } else {
        // Revert back to "Show More" in localized language
        button.textContent = loadText
      }
    })
    .catch((err) => {
      console.error('Error loading next page:', err)
      button.textContent = 'Error. Try again?'
    })
    .finally(() => {
      this.isLoading = false
    })
}



  // --------------------------------------------------------------------------
  // EXISTING FILTER / SORT LOGIC (AjaxRenderer-based)
  // --------------------------------------------------------------------------
  handleStickyHeaderChange(evt) {
    this.isStickyHeader = evt.detail.isSticky
    if (this.isStickyHeader) {
      this.setFilterStickyPosition()
    }
  }

  handleToggleMobileFilters(evt) {
    const { isOpen } = evt.detail
    if (isOpen) {
      document.dispatchEvent(
        new CustomEvent(EVENTS.sizeDrawer, {
          detail: { heights: [document.querySelector(this.selectors.filterBar).offsetHeight] }
        })
      )

      // Scroll to top of filter bar
      let scrollTo = this.getScrollFilterTop()
      window.scrollTo({ top: scrollTo, behavior: 'smooth' })
    }
  }

  initSort() {
    this.queryParams = new URLSearchParams(window.location.search)
    this.sortSelect = document.querySelector(this.selectors.sortSelect)

    if (this.sortSelect) {
      this.defaultSort = this.getDefaultSortValue()
      this.sortSelect.addEventListener(
        'change',
        () => {
          this.onSortChange()
        },
        { signal: this.abortController.signal }
      )
    }

    document.addEventListener(EVENTS.sortSelected, (evt) => {
      this.onSortChange(evt.detail.sortValue)
    })
  }

  getSortValue() {
    return this.sortSelect?.value || this.defaultSort
  }

  getDefaultSortValue() {
    return this.sortSelect?.getAttribute('data-default-sortby')
  }

  onSortChange(sortValue = null) {
    this.queryParams = new URLSearchParams(window.location.search)
    this.startLoading();
    // this.queryParams.delete('sort_by');

    if (sortValue) {
      this.queryParams.set('sort_by', sortValue)
    } else {
      this.queryParams.set('sort_by', this.getSortValue())
    }

    this.queryParams.delete('page')
    window.history.replaceState(null, '', '?' + this.queryParams.toString());

    // window.location.search = this.queryParams.toString()
    this.renderCollectionPage(this.queryParams);
  }

  initGridOptions() {
    const grid = this.querySelector(this.selectors.productGrid)
    const viewBtns = this.querySelectorAll(this.selectors.viewChange)
    viewBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        viewBtns.forEach((el) => el.classList.remove('is-active'))
        btn.classList.add('is-active')
        const newView = btn.dataset.view
        grid.dataset.view = newView
        this.updateAttribute('product_view', newView)
        window.dispatchEvent(new Event('resize'))
      }, { signal: this.abortController.signal })
    })
  }

  updateAttribute(key, value) {
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [key]: value } })
    }).then((response) => {
      if (!response.ok) throw response
      return response.json()
    })
  }

  initFilters() {
    const filterBar = document.querySelectorAll(this.selectors.filterBar)
    if (!filterBar.length) {
      return
    }

    this.bindBackButton()

    this.dispatchEvent(new CustomEvent(EVENTS.headerStickyCheck), { bubbles: true })
    if (this.isStickyHeader) {
      this.setFilterStickyPosition()

      document.addEventListener(
        'headerStickyChange',
        debounce(500, this.setFilterStickyPosition.bind(this)),
        { signal: this.abortController.signal }
      )
      window.addEventListener(
        'resize',
        debounce(500, this.setFilterStickyPosition.bind(this)),
        { signal: this.abortController.signal }
      )
    }

    document.querySelectorAll(this.selectors.activeTags).forEach((tag) => {
      tag.addEventListener('click', this.onTagClick.bind(this), { signal: this.abortController.signal })
    })

    document.querySelectorAll(this.selectors.tagsForm).forEach((form) => {
      form.addEventListener('input', this.onFormSubmit.bind(this), { signal: this.abortController.signal })
    })
  }

  initPriceRange() {
    document.addEventListener('price-range:change', this.onPriceRangeChange.bind(this), {
      once: true,
      signal: this.abortController.signal
    })
  }

  onPriceRangeChange(event) {
    this.renderFromFormData(event.detail)
  }

  renderActiveTag(parent, el) {
    const textEl = parent.querySelector('.tag__text')

    if (parent.classList.contains(this.classes.activeTag)) {
      parent.classList.remove(this.classes.activeTag)
    } else {
      parent.classList.add(this.classes.activeTag)

      if (el.closest('li').classList.contains(this.classes.removeTagParent)) {
        parent.remove()
      } else {
        document.querySelectorAll(this.selectors.activeTagList).forEach((list) => {
          const newTag = document.createElement('li')
          const newTagLink = document.createElement('a')
          newTag.classList.add('tag', 'tag--remove')
          newTagLink.classList.add('btn', 'btn--small')
          newTagLink.innerText = textEl.innerText
          newTag.appendChild(newTagLink)

          list.appendChild(newTag)
        })
      }
    }
  }

  onTagClick(evt) {
    const el = evt.currentTarget
    this.dispatchEvent(new Event('filter:selected', { bubbles: true }))

    if (el.classList.contains('no-ajax')) {
      return
    }

    evt.preventDefault()
    if (this.isAnimating) {
      return
    }

    this.isAnimating = true
    const parent = el.parentNode
    const newUrl = new URL(el.href)

    this.renderActiveTag(parent, el)
    this.updateScroll(true)
    this.startLoading()
    this.renderCollectionPage(newUrl.searchParams)
  }

  onFormSubmit(evt) {
    const el = evt.target
    this.dispatchEvent(new Event('filter:selected', { bubbles: true }))

    if (el.classList.contains('no-ajax')) {
      return
    }

    evt.preventDefault()
    if (this.isAnimating) {
      return
    }

    this.isAnimating = true
    const parent = el.closest('li')
    const formEl = el.closest('form')
    const formData = new FormData(formEl)

    this.renderActiveTag(parent, el)
    this.updateScroll(true)
    this.startLoading()
    this.renderFromFormData(formData)
  }

  // --------------------------------------------------------------------------
  // onReplaceAjaxContent: Used by AjaxRenderer for filters/sorting.
  // NOT used for "Load More" anymore.
  // --------------------------------------------------------------------------
  onReplaceAjaxContent(newDom, section) {
    const openCollapsibleIds = this.fetchOpenCollapsibleFilters()
    openCollapsibleIds.forEach((selector) => {
      newDom.querySelectorAll(`[data-collapsible-id=${selector}]`).forEach(this.openCollapsible.bind(this))
    })

    const newContentEl = newDom.getElementById(section.nodeId)
    if (!newContentEl) {
      return
    }

    document.getElementById(section.nodeId).innerHTML = newContentEl.innerHTML

    const page = document.getElementById(section.nodeId)
    const countEl = page.querySelector('.collection-filter__item--count')
    if (countEl) {
      const count = countEl.innerText
      document.querySelectorAll('[data-collection-count]').forEach((el) => {
        el.innerText = count
      })
    }
  }

  renderFromFormData(formData) {
    const searchParams = new URLSearchParams(formData)
    this.renderCollectionPage(searchParams)
  }

  // Renders a new page via AjaxRenderer for filters/sorting changes,
  // Not used for “Show More.”
  renderCollectionPage(searchParams, updateURLHash = true) {
    this.ajaxRenderer.renderPage(window.location.pathname, searchParams, updateURLHash).then(() => {
      this.init()
      this.updateScroll(false)

      this.dispatchEvent(new CustomEvent('collection:reloaded', { bubbles: true }))
      this.isAnimating = false
    })
  }

  updateScroll(animate) {
    const ajaxEl = document.getElementById('AjaxContent')
    if (!ajaxEl) return

    let scrollTo = ajaxEl.offsetTop
    if (this.isStickyHeader) {
      const headerEl = document.querySelector('#SiteHeader')
      if (headerEl) {
        scrollTo -= headerEl.offsetHeight
      }
    }

    if (!matchMedia('(max-width: 768px)').matches) {
      scrollTo -= 10
    }

    if (animate) {
      window.scrollTo({ top: scrollTo, behavior: 'smooth' })
    } else {
      window.scrollTo({ top: scrollTo })
    }
  }

  bindBackButton() {
    window.removeEventListener('popstate', this._popStateHandler)
    this._popStateHandler = (state) => {
      if (state) {
        const newUrl = new URL(window.location.href)
        this.renderCollectionPage(newUrl.searchParams, false)
      }
    }

    window.addEventListener('popstate', this._popStateHandler, { signal: this.abortController.signal })
  }

  fetchOpenCollapsibleFilters() {
    const openDesktopCollapsible = Array.from(
      document.querySelectorAll(
        `${this.selectors.sidebar} ${this.selectors.trigger}.${this.classes.isOpen}`
      )
    )
    const openMobileCollapsible = Array.from(
      document.querySelectorAll(
        `${this.selectors.inlineWrapper} ${this.selectors.trigger}.${this.classes.isOpen}`
      )
    )

    return [...openDesktopCollapsible, ...openMobileCollapsible].map((trigger) => trigger.dataset.collapsibleId)
  }

  openCollapsible(el) {
    if (el.classList.contains(this.classes.collapsibleContent)) {
      el.style.height = 'auto'
    }
    el.classList.add(this.classes.isOpen)
  }

  setFilterStickyPosition() {
    const siteHeader = document.querySelector('.site-header')
    if (!siteHeader) return
    const headerHeight = siteHeader.offsetHeight - 1

    const filterBar = document.querySelector(this.selectors.filterBar)
    if (filterBar) {
      filterBar.style.top = headerHeight + 'px'
    }

    // Also update top position of sticky sidebar
    const stickySidebar = this.querySelector('[data-sticky-sidebar]')
    if (stickySidebar) {
      stickySidebar.style.top = headerHeight + 30 + 'px'
    }
  }

  startLoading() {
    const gridWrapper = this.querySelector(this.selectors.collectionGrid)
    if (gridWrapper) {
      gridWrapper.classList.add('unload')
    }
  }

  forceReload() {
    this.init()
  }

  getScrollFilterTop() {
    const filterBar = document.querySelector(this.selectors.filterBar)
    if (!filterBar) return 0
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const elTop = filterBar.getBoundingClientRect().top
    return elTop + scrollTop
  }
}

customElements.define('item-grid', ItemGrid)
export default ItemGrid
